import type { DISCOVERY_KINDS, LAB_VENDORS } from '@/model/middleware-types'
import type { OrpcClient } from '@/orpc/client'
import type { Facility } from '../shared/facilityTypes'
import type { RadiologyFacilityWithModalities } from '../shared/useRadiologyFacilities'

export type DiscoveredFacility = Awaited<
  ReturnType<OrpcClient['facilityDiscoverySearch']>
>['facilities'][number]
export type FrequentFacilityList = Awaited<ReturnType<OrpcClient['facilityDiscoveryFrequent']>>
export type EnrichmentView = Awaited<
  ReturnType<OrpcClient['facilityDiscoveryEnrichment']>
>['items'][string]
export type DiscoveryModality = NonNullable<
  Parameters<OrpcClient['facilityDiscoverySearch']>[0]['modality']
>
export type DiscoveryKind = (typeof DISCOVERY_KINDS)[number]
export type LabVendor = (typeof LAB_VENDORS)[number]
export type FacilityForOrder = RadiologyFacilityWithModalities // what RadiologyRequisitionForm expects
export type LabFacilityForOrder = Facility // what the Labcorp/Quest forms expect

/** Shape the rest of RadiologyRequisitionForm (PDF, fax override, review) already consumes. */
export function toFacilityForOrder(
  f: DiscoveredFacility,
  organizationId: string,
  fax?: string,
): FacilityForOrder {
  return {
    id: organizationId,
    name: f.name,
    address: f.street,
    city: f.city,
    state: f.state,
    zip: f.zip,
    fax: fax ?? f.fax ?? '',
    lat: f.lat ?? null,
    lon: f.lon ?? null,
    modalities: [],
    pendingApproval: false,
    isOrganization: true,
  }
}

/** Same, for the Labcorp/Quest forms — their `Facility` carries no modalities. */
export function toLabFacilityForOrder(
  f: DiscoveredFacility,
  organizationId: string,
  fax?: string,
): LabFacilityForOrder {
  return {
    id: organizationId,
    name: f.name,
    address: f.street,
    city: f.city,
    state: f.state,
    zip: f.zip,
    fax: fax ?? f.fax ?? '',
    // A saved facility without a geolocation has no coordinates; the lab forms
    // model that as null, not undefined.
    lat: f.lat ?? null,
    lon: f.lon ?? null,
    // Adopted organizations carry no letterhead logo, same as a manually added one.
    logoUrl: undefined,
    pendingApproval: false,
    isOrganization: true,
  }
}

/**
 * The scroll offset that brings a row fully into a scrolling list, moving as little
 * as possible: a row above the viewport aligns to its top, one below aligns to its
 * bottom, and one already visible does not move. A row taller than the viewport
 * aligns to its top, so expanding it never scrolls its heading out of sight.
 * All values are in the list's content coordinates.
 */
export function scrollTopForRow(
  current: number,
  viewHeight: number,
  rowTop: number,
  rowHeight: number,
): number {
  if (rowTop < current) return rowTop
  const rowBottom = rowTop + rowHeight
  if (rowBottom > current + viewHeight) return Math.min(rowBottom - viewHeight, rowTop)
  return current
}

// Compendium modalities that have no search phrase of their own but are performed
// by the same facilities as a parent modality.
const MODALITY_ALIASES: Record<string, DiscoveryModality> = {
  ct_angiography: 'ct_scan',
  mr_angiography: 'mri',
  vascular_ultrasound: 'ultrasound',
  interventional_ultrasound: 'ultrasound',
}

/**
 * The single modality to search facilities for, derived from the procedures the
 * clinician picked in the previous step. Every procedure must resolve to the same
 * modality; a mixed order (e.g. MRI Brain + CT Chest) or one with no mappable
 * modality falls back to a general imaging search (undefined).
 */
export function discoveryModalityForProcedures(
  procedureModalityLabels: string[],
  options: { key: string; label: string }[],
  discoveryModalities: readonly string[],
): DiscoveryModality | undefined {
  const keys = new Set<DiscoveryModality>()
  for (const label of procedureModalityLabels) {
    const key = options.find((m) => m.label === label)?.key
    if (!key) return undefined
    const resolved = MODALITY_ALIASES[key] ?? (key as DiscoveryModality)
    if (!discoveryModalities.includes(resolved)) return undefined
    keys.add(resolved)
  }
  return keys.size === 1 ? [...keys][0] : undefined
}
